import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { COMPANIONS_NAMESPACE } from '@/i18n';
import { CompanionForm } from '@/Pages/Companions/components/CompanionForm';
import { CompanionRowItem } from '@/Pages/Companions/components/CompanionRowItem';
import { useCompanions } from '@/Pages/Companions/hooks/useCompanions';
import { isKnownMinorCompanion } from '@/Pages/Companions/utils/companionAge';

/**
 * Cadastro dos acompanhantes do titular. Vive DENTRO da tela do convite
 * (`/invite`) — antes ficava numa rota própria `/companions`, o que partia o
 * fluxo em duas telas para a mesma pessoa. Só é renderizada depois que o
 * titular tem biometria; quem decide isso é o `InvitePage`.
 *
 * O titular cria, edita e exclui daqui: a lista mostra o que já está
 * preenchido de cada um, o formulário é o mesmo para criar e para corrigir, e a
 * janela de alteração (até 2h do início do evento e antes do check-in) vem
 * decidida do service em `row.editable` — a UI não recalcula regra de negócio.
 */
export function CompanionsSection(): JSX.Element {
  const { t } = useTranslation(COMPANIONS_NAMESPACE);
  const companions = useCompanions();
  const [confirmRemove, setConfirmRemove] = useState<{ id: string; name: string } | null>(null);

  const {
    progress,
    rows,
    isLoading,
    loadError,
    reload,
    submitError,
    shareFeedback,
    isSubmitting,
    hasOpenSlot,
    uploadingFor,
    removingId,
    editingId,
    share,
    sendPhotoFor,
    startEdit,
    removeCompanion,
    finish,
  } = companions;

  const allDone = progress.declared > 0 && progress.pending === 0;
  const pendingRows = rows.filter((row) => row.status === 'awaiting_selfie');
  // Vaga pendente que ainda pode ser de criança (sem data de nascimento, ou já
  // marcada como menor): a seção diz com todas as letras que menor não recebe
  // link — quem envia a foto é o titular.
  const showMinorHint = pendingRows.some(
    (row) => row.birthDate === null || isKnownMinorCompanion(row.birthDate),
  );
  const isEditing = editingId !== null;
  const busy = isSubmitting || removingId !== null || uploadingFor !== null;

  // A lista é quem diz se sobra vaga e quem ainda falta. Decidir o layout com o
  // progresso guardado na sessão (velho) mostrava "0 de 1 com biometria" sem
  // lista e sem formulário — beco com um único botão "deixar para depois".
  if (isLoading) {
    return (
      <Box
        data-testid="companions-section"
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
      >
        <CircularProgress size={20} />
        <Typography component="p" variant="body2" data-testid="companions-loading">
          {t('loading')}
        </Typography>
      </Box>
    );
  }

  // Sem a lista não dá para saber o que já existe: em vez de sumir com tudo, a
  // tela explica e oferece "tentar de novo" (a volta pelo link também resolve).
  if (loadError !== null) {
    return (
      <Box
        data-testid="companions-section"
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <Typography component="h2" variant="h6">
          {t('title')}
        </Typography>
        <Alert severity="error" data-testid="companions-load-error">
          {t('loadFailed')}
        </Alert>
        <Typography component="p" variant="caption" color="text.secondary">
          {loadError}
        </Typography>
        <Button variant="contained" size="large" onClick={reload} data-testid="companions-retry">
          {t('retryCta')}
        </Button>
        <Button variant="text" onClick={finish} data-testid="companions-later">
          {t('laterCta')}
        </Button>
        <Typography component="p" variant="caption" color="text.secondary">
          {t('laterHint')}
        </Typography>
      </Box>
    );
  }

  // Convite sem acompanhante nenhum: não há o que cadastrar, só o comprovante.
  if (progress.declared === 0 && rows.length === 0) {
    return (
      <Box
        data-testid="companions-section"
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <Typography component="p" variant="body2" color="text.secondary">
          {t('noCompanions')}
        </Typography>
        <Button variant="contained" size="large" onClick={finish} data-testid="companions-finish">
          {t('receiptCta')}
        </Button>
      </Box>
    );
  }

  return (
    <Box data-testid="companions-section" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box component="header">
        <Typography component="h2" variant="h6">
          {t('title')}
        </Typography>
        <Typography component="p" variant="body2" color="text.secondary">
          {t('subtitle', { declared: progress.declared })}
        </Typography>
        <Typography component="p" variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
          {t('progress', { captured: progress.captured, declared: progress.declared })}
        </Typography>
      </Box>

      {rows.length > 0 && (
        <Box>
          <Typography component="h3" variant="subtitle2">
            {t('listTitle')}
          </Typography>
          {showMinorHint && (
            <Alert
              severity="info"
              icon={false}
              sx={{ mt: 1 }}
              data-testid="companions-minor-hint"
            >
              {t('minorRowsHint')}
            </Alert>
          )}
          <List dense disablePadding data-testid="companions-list">
            {rows.map((row) => (
              <CompanionRowItem
                key={row.id}
                row={row}
                isUploading={uploadingFor === row.id}
                isRemoving={removingId === row.id}
                busy={busy}
                onPhoto={(file) => void sendPhotoFor(row.id, file)}
                onShare={() => void share(row)}
                onEdit={() => startEdit(row)}
                onRemove={() =>
                  setConfirmRemove({ id: row.id, name: row.fullName || t('unnamedCompanion') })
                }
              />
            ))}
          </List>
          {shareFeedback === 'copied' && (
            <Alert severity="success" data-testid="companion-share-copied">
              {t('shareCopied')}
            </Alert>
          )}
          {shareFeedback === 'failed' && <Alert severity="error">{t('shareFailed')}</Alert>}
          {/* Erro de ação da LISTA (foto, exclusão): o formulário pode nem estar
              na tela quando ela falha, então a mensagem mora aqui. */}
          {submitError !== null && !isEditing && (
            <Alert severity="error" data-testid="companions-row-error">
              {submitError}
            </Alert>
          )}
        </Box>
      )}

      {/* Editar não consome vaga: o formulário de edição aparece mesmo com o
          convite lotado. */}
      {isEditing ? (
        <CompanionForm companions={companions} />
      ) : (
        <>
          {allDone && (
            <Alert severity="success" data-testid="companions-all-done">
              {t('allDone')}
            </Alert>
          )}

          {/* Vaga tomada por acompanhante sem biometria: o caminho é a linha
              dele na lista. O aviso NÃO depende de `rows.length` — era com a
              lista vazia que a tela ficava muda. */}
          {!allDone && !hasOpenSlot && (
            <Alert severity="info" data-testid="companions-no-open-slot">
              {pendingRows.length > 0
                ? t('noOpenSlot', { declared: progress.declared })
                : t('noOpenSlotEmpty')}
            </Alert>
          )}

          {hasOpenSlot && <CompanionForm companions={companions} />}

          {!hasOpenSlot && !allDone && (
            // "Atualizar" traz a lista de novo (a vaga pode ter sido resolvida
            // no aparelho do acompanhante) antes de o titular desistir.
            <Button
              variant="contained"
              size="large"
              onClick={reload}
              data-testid="companions-refresh"
            >
              {t('refreshCta')}
            </Button>
          )}

          {allDone ? (
            <Button
              variant="contained"
              size="large"
              onClick={finish}
              data-testid="companions-finish"
            >
              {t('finishCta')}
            </Button>
          ) : (
            <>
              <Button variant="text" onClick={finish} data-testid="companions-later">
                {t('laterCta')}
              </Button>
              <Typography component="p" variant="caption" color="text.secondary">
                {t('laterHint')}
              </Typography>
            </>
          )}
        </>
      )}

      {/* Excluir libera a vaga para outra pessoa e tira a biometria do evento —
          confirma antes, dizendo o que acontece. */}
      <Dialog open={confirmRemove !== null} onClose={() => setConfirmRemove(null)}>
        <DialogTitle>{t('removeConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText data-testid="companion-remove-confirm-body">
            {t('removeConfirmBody', { name: confirmRemove?.name ?? '' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemove(null)} data-testid="companion-remove-cancel">
            {t('removeCancelCta')}
          </Button>
          <Button
            color="error"
            variant="contained"
            data-testid="companion-remove-confirm"
            onClick={() => {
              const target = confirmRemove;
              setConfirmRemove(null);
              if (target !== null) void removeCompanion(target.id);
            }}
          >
            {t('removeConfirmCta')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
